import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, ApiError, type AuditEvent, type AuditFilters } from '../api';
import { Alert, Badge, Button, Card, EmptyState, PageHeader, TableSkeleton } from '../components/ui';
import { IconShield } from '../components/icons';
import {
  actionGroup,
  actionLabel,
  changesOf,
  entityLabel,
  fieldLabel,
  groupLabel,
  valueLabel,
} from '../audit';

/**
 * The audit trail, read back (SPEC §8 item 17).
 *
 * Events have been written on every action since phase 1 and nothing could read
 * them, which makes an audit trail a promise rather than a control. This is the
 * screen that turns it into one.
 *
 * Reading the trail is deliberately not itself audited: it would be the most
 * common event in the table inside a week, and a log whose bulk is people
 * looking at the log is a log nobody looks at.
 */
export function AuditLog() {
  const [filters, setFilters] = useState<AuditFilters>({ page: 1 });
  const [open, setOpen] = useState<string | null>(null);

  const page = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => api.audit(filters),
    placeholderData: keepPreviousData,
  });

  // Any filter change resets to page 1, otherwise you narrow the results and
  // land on an empty page 7.
  const set = (patch: AuditFilters) => setFilters({ ...filters, ...patch, page: 1 });

  if (page.isPending) {
    return (
      <div>
        <PageHeader title="Audit log" description="Every action this system has recorded." />
        <TableSkeleton rows={8} columns={4} />
      </div>
    );
  }
  if (page.isError) {
    return <Alert>{page.error instanceof ApiError ? page.error.message : 'Could not open the audit log.'}</Alert>;
  }

  const data = page.data;
  const groups = [...new Set(data.actions.map((row) => actionGroup(row.action)))].sort();
  const active = Boolean(filters.action || filters.actorUserId || filters.from || filters.to);

  return (
    <div>
      <PageHeader
        title="Audit log"
        description="Every action this system has recorded, oldest at the bottom. The trail is append-only: nothing here can be edited or removed."
        actions={
          <>
            <Badge tone="neutral">{data.total} recorded</Badge>
            <Button
              variant="secondary"
              onClick={() => window.open(api.auditExportUrl(filters), '_blank')}
              disabled={data.total === 0}
            >
              Export CSV
            </Button>
          </>
        }
      />

      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Picker
            label="What happened"
            value={filters.action ?? ''}
            onChange={(value) => set({ action: value || undefined })}
            options={[
              // Groups first, as a prefix filter, then the individual actions.
              // "Requests" is what somebody actually wants nine times in ten.
              ...groups.map((group) => ({ value: group, label: groupLabel(group) })),
              ...data.actions.map((row) => ({
                value: row.action,
                label: `   ${actionLabel(row.action)} (${row.count})`,
              })),
            ]}
          />

          <Picker
            label="Who"
            value={filters.actorUserId ?? ''}
            onChange={(value) => set({ actorUserId: value || undefined })}
            options={data.actors.map((actor) => ({ value: actor.id, label: actor.name }))}
          />

          <DateBox label="From" value={filters.from ?? ''} onChange={(value) => set({ from: value || undefined })} />
          <DateBox label="To" value={filters.to ?? ''} onChange={(value) => set({ to: value || undefined })} />

          {active && (
            <Button size="sm" variant="ghost" onClick={() => setFilters({ page: 1 })}>
              Clear
            </Button>
          )}
        </div>
      </Card>

      {data.events.length === 0 ? (
        <EmptyState
          icon={<IconShield className="h-6 w-6" />}
          title={active ? 'Nothing matches those filters' : 'Nothing recorded yet'}
          body={
            active
              ? 'Widen the dates, or clear the filters to see everything.'
              : 'Actions are recorded as people use the system. This fills itself.'
          }
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-3xl text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                    <th className="px-5 py-3">When</th>
                    <th className="px-5 py-3">Who</th>
                    <th className="px-5 py-3">What</th>
                    <th className="px-5 py-3">On</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((event) => (
                    <Row
                      key={event.id}
                      event={event}
                      open={open === event.id}
                      onToggle={() => setOpen(open === event.id ? null : event.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {data.pages > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <Button
                size="sm"
                variant="secondary"
                disabled={data.page <= 1}
                onClick={() => setFilters({ ...filters, page: data.page - 1 })}
              >
                Previous
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Page {data.page} of {data.pages} · {data.total} events
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={data.page >= data.pages}
                onClick={() => setFilters({ ...filters, page: data.page + 1 })}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({ event, open, onToggle }: { event: AuditEvent; open: boolean; onToggle: () => void }) {
  const changes = changesOf(event);
  const when = new Date(event.createdAt);

  return (
    <>
      <tr className="animate-row border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
        <td className="px-5 py-3 whitespace-nowrap text-slate-600 tabular-nums dark:text-slate-400">
          {when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </td>
        <td className="px-5 py-3">
          {event.actor ? (
            <>
              <div className="text-slate-900 dark:text-slate-100">{event.actor.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{event.actor.email}</div>
            </>
          ) : (
            // Users are deactivated rather than deleted, so this is rare, but
            // an audit row with a blank actor would read as a bug.
            <span className="text-slate-500 dark:text-slate-400">Account removed</span>
          )}
        </td>
        <td className="px-5 py-3 text-slate-900 dark:text-slate-100">{actionLabel(event.action)}</td>
        <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
          {event.entityType === 'Request' && event.entityId ? (
            <Link
              to={`/requests/${event.entityId}`}
              className="text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
            >
              the request
            </Link>
          ) : (
            entityLabel(event.entityType)
          )}
        </td>
        <td className="px-5 py-3 text-right">
          {changes.length > 0 && (
            <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={open}>
              {open ? 'Hide' : `${changes.length} change${changes.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </td>
      </tr>

      {open && changes.length > 0 && (
        <tr className="border-b border-slate-100 bg-slate-50/60 dark:border-slate-800/70 dark:bg-slate-900/50">
          <td colSpan={5} className="px-5 py-3">
            <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-[auto_1fr]">
              {changes.map((change) => (
                <div key={change.field} className="contents">
                  <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {fieldLabel(change.field)}
                  </dt>
                  <dd className="text-xs text-slate-700 dark:text-slate-300">
                    <span className="text-slate-500 line-through dark:text-slate-400">
                      {valueLabel(change.from)}
                    </span>{' '}
                    → <span className="font-medium">{valueLabel(change.to)}</span>
                  </dd>
                </div>
              ))}
            </dl>
            {event.ip && (
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">From {event.ip}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      >
        <option value="">Anything</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DateBox({ label, value, onChange }: { label: string; value: string; onChange: (next: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      />
    </label>
  );
}
